import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Plus, X, Save, Calendar,
  CheckCircle2, Clock, Flag, Tag, Users, Edit2, Trash2, Check
} from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, parseISO
} from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Task, Profile, TASK_CATEGORIES } from '@/lib/types';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-blue-400',
  medium: 'bg-accent',
  high: 'bg-destructive',
};

const EMPTY_FORM = {
  title: '', description: '', due_date: '', due_time: '',
  category: 'personal', status: 'pending', priority: 'medium', tagged_partners: [] as string[],
};

export default function Schedule() {
  const { user, profile: myProfile } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [partners, setPartners] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase.from('tasks').select('*').or(`owner_id.eq.${user.id},tagged_partners.cs.{${user.id}}`).order('due_date', { ascending: true });
    setTasks((data as Task[]) || []);
    setLoading(false);
  }, [user]);

  const fetchPartners = useCallback(async () => {
    if (!user) return;
    const { data: friendships } = await supabase
      .from('friendships')
      .select('requester_id, addressee_id')
      .eq('status', 'accepted')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);
    if (!friendships?.length) return;
    const ids = friendships.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id);
    const { data } = await supabase.from('profiles').select('*').in('id', ids);
    setPartners((data as Profile[]) || []);
  }, [user]);

  useEffect(() => { fetchTasks(); fetchPartners(); }, [fetchTasks, fetchPartners]);

  const calendarDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 }),
  });

  const tasksForDate = (date: Date) =>
    tasks.filter(t => t.due_date && isSameDay(parseISO(t.due_date), date));

  const selectedDateTasks = tasksForDate(selectedDate);
  const ownTasks = selectedDateTasks.filter(t => t.owner_id === user?.id);
  const taggedTasks = selectedDateTasks.filter(t => t.owner_id !== user?.id);
  const ownUpcoming = tasks.filter(t => t.owner_id === user?.id && t.status !== 'completed' && t.status !== 'cancelled');
  const taggedUpcoming = tasks.filter(t => t.owner_id !== user?.id && t.status !== 'completed' && t.status !== 'cancelled');

  function openNew() {
    setEditingTask(null);
    setForm({ ...EMPTY_FORM, due_date: format(selectedDate, 'yyyy-MM-dd') });
    setShowModal(true);
  }

  function openEdit(task: Task) {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      due_time: task.due_time || '',
      category: task.category,
      status: task.status,
      priority: task.priority,
      tagged_partners: task.tagged_partners || [],
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Task title is required'); return; }
    if (!user) return;
    setSaving(true);
    if (editingTask) {
      const { error } = await supabase.from('tasks').update({
        title: form.title, description: form.description || null,
        due_date: form.due_date || null, due_time: form.due_time || null,
        category: form.category, status: form.status, priority: form.priority,
        tagged_partners: form.tagged_partners,
      }).eq('id', editingTask.id);
      if (error) { toast.error('Failed to update task'); setSaving(false); return; }
      toast.success('Task updated!');
    } else {
      const { error } = await supabase.from('tasks').insert({
        owner_id: user.id, title: form.title,
        description: form.description || null,
        due_date: form.due_date || null, due_time: form.due_time || null,
        category: form.category, status: form.status, priority: form.priority,
        tagged_partners: form.tagged_partners,
      });
      if (error) { toast.error('Failed to create task'); setSaving(false); return; }
      toast.success('Task created!');
      // Notify each tagged partner via chat
      if (form.tagged_partners.length > 0) {
        await sendTaskTagNotifications(form.tagged_partners, form.title, form.description, form.due_date);
      }
    }
    setSaving(false);
    setShowModal(false);
    fetchTasks();
  }

  async function sendTaskTagNotifications(partnerIds: string[], title: string, description: string, dueDate: string) {
    if (!user) return;
    const creatorName = myProfile?.full_name || myProfile?.username || 'Your partner';
    for (const partnerId of partnerIds) {
      const p1 = user.id < partnerId ? user.id : partnerId;
      const p2 = user.id < partnerId ? partnerId : user.id;
      const { data: conv } = await supabase
        .from('conversations')
        .upsert({ participant1_id: p1, participant2_id: p2 }, { onConflict: 'participant1_id,participant2_id' })
        .select().single();
      if (!conv) continue;
      await supabase.from('messages').insert({
        conversation_id: conv.id,
        sender_id: user.id,
        content: JSON.stringify({ task_title: title, task_description: description, due_date: dueDate, creator_name: creatorName }),
        message_type: 'task_tag',
        image_url: null,
      });
    }
  }

  async function deleteTask(id: string) {
    await supabase.from('tasks').delete().eq('id', id);
    toast.success('Task deleted');
    fetchTasks();
  }

  async function toggleStatus(task: Task) {
    const next = task.status === 'completed' ? 'pending' : 'completed';
    await supabase.from('tasks').update({ status: next }).eq('id', task.id);
    setTasks(ts => ts.map(t => t.id === task.id ? { ...t, status: next } : t));
    // If a tagged partner is marking done, notify the task creator
    if (next === 'completed' && user && task.owner_id !== user.id) {
      await sendTaskDoneNotification(task);
    }
  }

  async function sendTaskDoneNotification(task: Task) {
    if (!user || !task.owner_id) return;
    const partnerName = myProfile?.full_name || myProfile?.username || 'Your partner';
    const ownerId = task.owner_id;
    const p1 = user.id < ownerId ? user.id : ownerId;
    const p2 = user.id < ownerId ? ownerId : user.id;
    const { data: conv } = await supabase
      .from('conversations')
      .upsert({ participant1_id: p1, participant2_id: p2 }, { onConflict: 'participant1_id,participant2_id' })
      .select().single();
    if (!conv) return;
    await supabase.from('messages').insert({
      conversation_id: conv.id,
      sender_id: user.id,
      content: JSON.stringify({
        task_title: task.title,
        partner_name: partnerName,
        message_subtype: 'completion',
      }),
      message_type: 'task_tag',
      image_url: null,
    });
  }

  function togglePartner(id: string) {
    setForm(f => ({
      ...f,
      tagged_partners: f.tagged_partners.includes(id)
        ? f.tagged_partners.filter(p => p !== id)
        : [...f.tagged_partners, id],
    }));
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Schedule</h1>
            <p className="text-muted-foreground">Track your goals and tag partners to stay accountable</p>
          </div>
          <Button onClick={openNew} className="rounded-xl gap-2"
            style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
            <Plus className="w-4 h-4" /> New Task
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* @section: calendar */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-2xl overflow-hidden"
              style={{ boxShadow: '0 4px 20px color-mix(in srgb, var(--primary) 7%, transparent)' }}>
              {/* Calendar header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h2 className="text-lg font-bold text-foreground">{format(currentMonth, 'MMMM yyyy')}</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setCurrentMonth(new Date())}
                    className="px-3 h-8 rounded-lg hover:bg-muted text-xs font-medium transition-colors">Today</button>
                  <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-border">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, i) => {
                  const dayTasks = tasksForDate(day);
                  const selected = isSameDay(day, selectedDate);
                  const today = isToday(day);
                  const inMonth = isSameMonth(day, currentMonth);
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDate(day)}
                      className={`min-h-[80px] p-1.5 flex flex-col items-start border-b border-r border-border text-left transition-all duration-150 ${
                        selected ? 'bg-primary/8' : inMonth ? 'hover:bg-muted/50' : 'opacity-40'
                      }`}
                    >
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1 ${
                        today && selected ? 'bg-primary text-primary-foreground'
                          : today ? 'bg-primary/15 text-primary'
                          : selected ? 'bg-muted text-foreground'
                          : 'text-foreground'
                      }`}>
                        {format(day, 'd')}
                      </span>
                      <div className="flex flex-col gap-0.5 w-full overflow-hidden">
                        {dayTasks.slice(0, 2).map(t => (
                          <div key={t.id} className={`w-full rounded px-1 py-0.5 text-xs truncate ${
                            t.status === 'completed'
                              ? 'line-through text-muted-foreground bg-muted/50'
                              : t.owner_id === user?.id
                              ? 'bg-accent/20 text-accent'
                              : 'bg-primary/10 text-primary'
                          }`}>
                            {t.title}
                          </div>
                        ))}
                        {dayTasks.length > 2 && (
                          <span className="text-xs text-muted-foreground pl-1">+{dayTasks.length - 2} more</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* @section: day-sidebar */}
          <div className="flex flex-col gap-4">
            <div className="bg-card border border-border rounded-2xl p-5"
              style={{ boxShadow: '0 4px 20px color-mix(in srgb, var(--primary) 7%, transparent)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-foreground">{format(selectedDate, 'EEEE')}</h3>
                  <p className="text-sm text-muted-foreground">{format(selectedDate, 'MMMM d, yyyy')}</p>
                </div>
                <Button size="sm" onClick={openNew} variant="outline" className="rounded-xl gap-1">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-5">
                  {/* ── Own Tasks ── */}
                  <div>
                    <p className="text-[11px] font-semibold text-accent uppercase tracking-wider mb-2">Own Tasks</p>
                    {ownTasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic py-1.5 pl-1">No Tasks for Today!</p>
                    ) : (
                      <div className="space-y-2">
                        {ownTasks.map(task => {
                          const taggedProfiles = (task.tagged_partners || [])
                            .map(pid => partners.find(p => p.id === pid))
                            .filter(Boolean) as Profile[];
                          const names = taggedProfiles.map(p => p.full_name || p.username).join(', ');
                          const partnerLabel = taggedProfiles.length === 0 ? null
                            : taggedProfiles.length === 1 ? `${names} is tagged`
                            : `${names} have been tagged`;
                          return (
                            <motion.div key={task.id} layout
                              className="group bg-background border border-border rounded-xl p-3 flex items-start gap-3 hover:border-accent/30 transition-all duration-150">
                              {taggedProfiles.length === 0 && (
                                <button
                                  onClick={() => task.status !== 'cancelled' && toggleStatus(task)}
                                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                                    task.status === 'completed' ? 'bg-green-500 border-green-500'
                                    : task.status === 'cancelled' ? 'border-muted-foreground/30 opacity-40 cursor-not-allowed'
                                    : task.status === 'in_progress' ? 'border-accent hover:border-accent/70'
                                    : 'border-border hover:border-accent'}`}
                                  title={task.status === 'cancelled' ? 'Cancelled' : task.status === 'completed' ? 'Mark incomplete' : 'Mark complete'}
                                >
                                  {task.status === 'completed' && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                  {task.status === 'cancelled' && <X className="w-2.5 h-2.5 text-muted-foreground" />}
                                  {task.status === 'in_progress' && <div className="w-2 h-2 rounded-sm bg-accent" />}
                                </button>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium leading-tight ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{task.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority]}`} />
                                  <span className="text-xs text-muted-foreground capitalize">{task.priority}</span>
                                  {task.due_time && <span className="text-xs text-muted-foreground">{task.due_time.slice(0, 5)}</span>}
                                </div>
                                {partnerLabel && (
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    {taggedProfiles.slice(0, 3).map(p => (
                                      <Avatar key={p.id} className="w-4 h-4 flex-shrink-0">
                                        <AvatarImage src={p.avatar_url ?? undefined} />
                                        <AvatarFallback className="text-[8px] bg-accent/15 text-accent">{(p.full_name || p.username)?.[0]?.toUpperCase()}</AvatarFallback>
                                      </Avatar>
                                    ))}
                                    <span className="text-xs text-muted-foreground">{partnerLabel}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                {task.status === 'completed' && taggedProfiles.length > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-500/10 text-green-600 border border-green-500/20">
                                    <Check className="w-2.5 h-2.5" strokeWidth={3} /> Done
                                  </span>
                                )}
                                <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => openEdit(task)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => deleteTask(task.id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ── Follow Ups ── */}
                  <div>
                    <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-2">Follow Ups</p>
                    {taggedTasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic py-1.5 pl-1">No Follow Ups Today!</p>
                    ) : (
                      <div className="space-y-2">
                        {taggedTasks.map(task => {
                          const owner = partners.find(p => p.id === task.owner_id);
                          const todayStr = format(new Date(), 'yyyy-MM-dd');
                          const isOverdue = !!task.due_date && task.due_date < todayStr
                            && task.status !== 'completed' && task.status !== 'cancelled';
                          return (
                            <motion.div key={task.id} layout
                              className="group bg-background border border-border rounded-xl p-3 flex items-start gap-3 hover:border-primary/30 transition-all duration-150">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium leading-tight ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{task.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority]}`} />
                                  <span className="text-xs text-muted-foreground capitalize">{task.priority}</span>
                                  {task.due_time && <span className="text-xs text-muted-foreground">{task.due_time.slice(0, 5)}</span>}
                                </div>
                                {owner && (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    <Avatar className="w-4 h-4 flex-shrink-0">
                                      <AvatarImage src={owner.avatar_url ?? undefined} />
                                      <AvatarFallback className="text-[8px] bg-primary/15 text-primary">{(owner.full_name || owner.username)?.[0]?.toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <span className="text-xs text-muted-foreground">{owner.full_name || owner.username} has tagged you</span>
                                  </div>
                                )}
                              </div>
                              {/* Action column */}
                              <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5">
                                {task.status === 'completed' ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-500/10 text-green-600 border border-green-500/20">
                                      <Check className="w-2.5 h-2.5" strokeWidth={3} /> Done
                                    </span>
                                    <button
                                      onClick={() => toggleStatus(task)}
                                      className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                                    >
                                      Undo
                                    </button>
                                  </>
                                ) : task.status !== 'cancelled' ? (
                                  <>
                                    {isOverdue && (
                                      <span className="text-[10px] font-semibold text-destructive uppercase tracking-wide leading-none">
                                        Overdue
                                      </span>
                                    )}
                                    <button
                                      onClick={() => toggleStatus(task)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors whitespace-nowrap"
                                    >
                                      <Check className="w-2.5 h-2.5" strokeWidth={3} /> Mark Done
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Upcoming */}
            <div className="bg-card border border-border rounded-2xl p-5"
              style={{ boxShadow: '0 4px 20px color-mix(in srgb, var(--primary) 7%, transparent)' }}>
              <h3 className="font-bold text-foreground mb-3 text-sm">Upcoming</h3>
              <div className="max-h-72 overflow-y-auto pr-1 space-y-4">
                {/* Own Tasks upcoming */}
                <div>
                  <p className="text-[11px] font-semibold text-accent uppercase tracking-wider mb-1.5">Own Tasks</p>
                  {ownUpcoming.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-1">No Tasks for Today!</p>
                  ) : (
                    <div className="space-y-1.5">
                      {ownUpcoming.slice(0, 5).map(t => (
                        <div key={t.id} className="flex items-center gap-2.5 py-1">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_COLORS[t.priority]}`} />
                          <span className="text-sm text-foreground truncate flex-1">{t.title}</span>
                          {t.due_date && <span className="text-xs text-muted-foreground flex-shrink-0">{format(parseISO(t.due_date), 'MMM d')}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Follow Ups upcoming */}
                <div>
                  <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-1.5">Follow Ups</p>
                  {taggedUpcoming.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-1">No Follow Ups Today!</p>
                  ) : (
                    <div className="space-y-1.5">
                      {taggedUpcoming.slice(0, 5).map(t => (
                        <div key={t.id} className="flex items-center gap-2.5 py-1">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_COLORS[t.priority]}`} />
                          <span className="text-sm text-foreground truncate flex-1">{t.title}</span>
                          {t.due_date && <span className="text-xs text-muted-foreground flex-shrink-0">{format(parseISO(t.due_date), 'MMM d')}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* @section: task-modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={springGentle}
              className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
            >
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-lg font-bold text-foreground">{editingTask ? 'Edit Task' : 'New Task'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <Label>Title <span className="text-destructive">*</span></Label>
                  <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="What do you want to accomplish?" className="mt-1.5 rounded-xl h-10" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Any details…" className="mt-1.5 rounded-xl resize-none" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Due Date</Label>
                    <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                      className="mt-1.5 rounded-xl h-10" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Due Time</Label>
                    <Input type="time" value={form.due_time} onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))}
                      className="mt-1.5 rounded-xl h-10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Category</Label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="mt-1.5 rounded-xl h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                      <SelectTrigger className="mt-1.5 rounded-xl h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {partners.length > 0 && (
                  <div>
                    <Label className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Tag Partners</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {partners.map(p => (
                        <button key={p.id} type="button" onClick={() => togglePartner(p.id)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all ${
                            form.tagged_partners.includes(p.id)
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                          }`}>
                          <Avatar className="w-5 h-5">
                            <AvatarImage src={p.avatar_url ?? undefined} />
                            <AvatarFallback className="text-xs bg-primary/15 text-primary">{(p.full_name || p.username)?.[0]}</AvatarFallback>
                          </Avatar>
                          {p.full_name || p.username}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowModal(false)} className="rounded-xl">Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} className="rounded-xl"
                    style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
                    <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : editingTask ? 'Save Changes' : 'Create Task'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
